/**
 * Call Recordings panel — expandable list of recordings for a call.
 * Used inside call history detail view.
 */

import { useState, useEffect, useRef } from 'react';
import { Play, Pause, Download, Loader2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallStore } from '@/stores/call-store';
import { usePermissions } from '@/hooks/use-permissions';
import type { CallRecording } from '@/types/voice';

interface CallRecordingsProps {
  callId: string;
  className?: string;
}

export function CallRecordings({ callId, className }: CallRecordingsProps) {
  const fetchCallRecordings = useCallStore((s) => s.fetchCallRecordings);
  const [recordings, setRecordings] = useState<CallRecording[]>([]);
  const [loading, setLoading] = useState(true);
  const { callPermissions } = usePermissions();

  // If user doesn't have permission to view recordings, show restricted message
  if (!callPermissions.canViewRecordings) {
    return (
      <div className={cn('flex items-center gap-2 text-xs text-muted-foreground py-2', className)}>
        <Lock className="h-3 w-3" />
        Recording access restricted
      </div>
    );
  }

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchCallRecordings(callId).then((recs) => {
      if (mounted) {
        setRecordings(recs);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, [callId, fetchCallRecordings]);

  if (loading) {
    return (
      <div className={cn('flex items-center gap-2 text-xs text-muted-foreground py-2', className)}>
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading recordings...
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <p className={cn('text-xs text-muted-foreground py-2', className)}>
        No recordings
      </p>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {recordings.map((rec) => (
        <RecordingItem key={rec.id} recording={rec} />
      ))}
    </div>
  );
}

function RecordingItem({ recording }: { recording: CallRecording }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(parseInt(recording.duration) || 0);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
    } else {
      void audio.play().catch(() => setPlaying(false));
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onLoadedMetadata = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };

    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(audio.duration || 0);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const formatTime = (time: number) => {
    const totalSeconds = Math.floor(Number.isFinite(time) ? time : 0);
    return `${Math.floor(totalSeconds / 60)}:${(totalSeconds % 60).toString().padStart(2, '0')}`;
  };

  const seek = (value: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(value)) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  return (
    <div className="flex items-center gap-2 rounded-lg bg-muted/30 px-3 py-2">
      <audio ref={audioRef} src={recording.url} preload="none" />
      <button
        onClick={togglePlay}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
      >
        {playing ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          <Play className="h-3.5 w-3.5 ml-0.5" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="relative flex h-5 items-center">
          <div className="absolute inset-x-0 h-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary/60"
              style={{ width: `${duration ? Math.min((currentTime / duration) * 100, 100) : 0}%` }}
            />
          </div>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(currentTime, duration || currentTime)}
            onChange={(event) => seek(Number(event.target.value))}
            disabled={!duration}
            aria-label="Seek recording"
            className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent accent-primary disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>

      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>

      <a
        href={recording.url}
        download
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        title="Download recording"
      >
        <Download className="h-3 w-3" />
      </a>

    </div>
  );
}
